"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.conditionMetricKeys = exports.RealtimeScoringDefinition = exports.ScoreRule = exports.ScoringCondition = exports.LocalMetricDefinition = exports.FormulaExpression = exports.MAX_LOCAL_METRICS = exports.PredicateValue = exports.MoneyValue = exports.MetricValueType = exports.MetricOperator = void 0;
const effect_1 = require("effect");
/**
 * The comparison operators that the realtime scoring engine can compile.
 *
 * The metric catalog, rather than a caller, decides which of these are valid
 * for a particular metric.
 */
exports.MetricOperator = effect_1.Schema.Literals(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn', 'contains']);
/** The storage and comparison shape of a metric value. */
exports.MetricValueType = effect_1.Schema.Literals([
    'number',
    'number_array',
    'boolean',
    'string',
    'string_array',
    'date',
    'money',
]);
/**
 * A monetary value always carries its original currency. The first realtime
 * scoring release only compares money in the same currency; FX conversion is a
 * separate policy and must never be implicit in a scoring definition.
 */
exports.MoneyValue = effect_1.Schema.Struct({
    amount: effect_1.Schema.Finite,
    currency: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(3)), effect_1.Schema.check(effect_1.Schema.isMaxLength(3))),
});
/** A literal used by a metric predicate. */
exports.PredicateValue = effect_1.Schema.Union([
    effect_1.Schema.String,
    effect_1.Schema.Finite,
    effect_1.Schema.Boolean,
    effect_1.Schema.Array(effect_1.Schema.String),
    effect_1.Schema.Array(effect_1.Schema.Finite),
    exports.MoneyValue,
]);
/** Keeps formula validation and the eventual dependency planner bounded. */
exports.MAX_LOCAL_METRICS = 25;
const FormulaMetricReferenceSchema = effect_1.Schema.Struct({
    kind: effect_1.Schema.Literal('metric'),
    metric: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
});
const FormulaNumberLiteralSchema = effect_1.Schema.Struct({
    kind: effect_1.Schema.Literal('number'),
    value: effect_1.Schema.Finite,
});
/** Runtime schema for the recursive, backend-compiled formula tree. */
exports.FormulaExpression = effect_1.Schema.suspend(() => effect_1.Schema.Union([
    FormulaMetricReferenceSchema,
    FormulaNumberLiteralSchema,
    effect_1.Schema.Struct({
        kind: effect_1.Schema.Literals(['add', 'subtract', 'multiply', 'divide']),
        left: exports.FormulaExpression,
        right: exports.FormulaExpression,
    }),
    effect_1.Schema.Struct({
        kind: effect_1.Schema.Literal('convertCurrency'),
        expression: exports.FormulaExpression,
        targetCurrency: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isPattern(/^[A-Z]{3}$/u))),
    }),
])).annotate({ identifier: 'RealtimeScoringFormulaExpression' });
/** A named, filter-local metric constructed from approved metric references. */
exports.LocalMetricDefinition = effect_1.Schema.Struct({
    id: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.check(effect_1.Schema.isMaxLength(64)), effect_1.Schema.check(effect_1.Schema.isPattern(/^[a-z][a-z0-9-]*$/u))),
    displayName: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.check(effect_1.Schema.isMaxLength(120))),
    expression: exports.FormulaExpression,
});
const MetricPredicateSchema = effect_1.Schema.Struct({
    kind: effect_1.Schema.Literal('predicate'),
    metric: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    operator: exports.MetricOperator,
    value: exports.PredicateValue,
});
const RatioConditionSchema = effect_1.Schema.Struct({
    kind: effect_1.Schema.Literal('ratio'),
    id: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1)), effect_1.Schema.check(effect_1.Schema.isMaxLength(64)), effect_1.Schema.check(effect_1.Schema.isPattern(/^[a-z][a-z0-9-]*$/u))),
    numeratorMetric: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    denominatorMetric: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    operator: exports.MetricOperator,
    value: effect_1.Schema.Finite,
});
/** Runtime schema for the recursive scoring condition tree. */
exports.ScoringCondition = effect_1.Schema.suspend(() => effect_1.Schema.Union([
    MetricPredicateSchema,
    RatioConditionSchema,
    effect_1.Schema.Struct({
        kind: effect_1.Schema.Literal('all'),
        conditions: effect_1.Schema.Array(exports.ScoringCondition),
    }),
    effect_1.Schema.Struct({
        kind: effect_1.Schema.Literal('any'),
        conditions: effect_1.Schema.Array(exports.ScoringCondition),
    }),
    effect_1.Schema.Struct({
        kind: effect_1.Schema.Literal('not'),
        condition: exports.ScoringCondition,
    }),
])).annotate({ identifier: 'RealtimeScoringCondition' });
/** A score contribution that is applied when its condition matches. */
exports.ScoreRule = effect_1.Schema.Struct({
    id: effect_1.Schema.String.pipe(effect_1.Schema.check(effect_1.Schema.isMinLength(1))),
    when: exports.ScoringCondition,
    score: effect_1.Schema.Finite,
});
/**
 * The versioned, declarative definition for the new realtime scoring engine.
 * It contains no stored score, explanation or compiled SQL.
 */
exports.RealtimeScoringDefinition = effect_1.Schema.Struct({
    version: effect_1.Schema.Literal('v1'),
    /** Optional so every previously stored V1 definition remains valid. */
    localMetrics: effect_1.Schema.optional(effect_1.Schema.Array(exports.LocalMetricDefinition).pipe(effect_1.Schema.check(effect_1.Schema.isMaxLength(exports.MAX_LOCAL_METRICS)))),
    eligibility: exports.ScoringCondition,
    scoreRules: effect_1.Schema.Array(exports.ScoreRule),
});
/**
 * Every metric a condition tree references, in traversal order.
 *
 * Duplicates are kept: callers that want a set build one, and the ones that
 * report on a definition would otherwise lose the fact that a metric is used
 * more than once.
 */
const conditionMetricKeys = (condition) => {
    switch (condition.kind) {
        case 'predicate':
            return [condition.metric];
        case 'ratio':
            return [condition.numeratorMetric, condition.denominatorMetric];
        case 'all':
        case 'any':
            return condition.conditions.flatMap(exports.conditionMetricKeys);
        case 'not':
            return (0, exports.conditionMetricKeys)(condition.condition);
    }
};
exports.conditionMetricKeys = conditionMetricKeys;
//# sourceMappingURL=model.js.map